import { createSlice, combineReducers, type PayloadAction } from '@reduxjs/toolkit';

import { negociacaoCasoStatusUpdated } from '@/features/messaging';
import type { NegociacaoCasoListItem, NegociacaoTabCounts } from '../types';
import type {
  NegociacoesFilters,
  NegociacoesSortConfig,
  NegociacoesTabValue,
} from '../types/negociacoesUiTypes';
import {
  archiveNegociacaoCaso,
  closeNegociacaoCaso,
  encerrarNegociacaoCaso,
  exportNegociacaoCasosCsv,
  fetchNegociacaoCasos,
  fetchNegociacaoPeriodoPreview,
  fetchNegociacaoSituacoes,
  type NegociacaoSituacaoOption,
  pauseNegociacaoCaso,
  reabrirNegociacaoCaso,
  resumeNegociacaoCaso,
  unarchiveNegociacaoCaso,
} from './actions';
import batchNegociacaoReducer from './batchSlice';
import loteDetailReducer from './loteDetailSlice';
import ajustesNegociacaoReducer from './ajustesNegociacaoSlice';
import lotesNegociacaoReducer from './lotesSlice';

interface NegociacoesState {
  items: NegociacaoCasoListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  isExportingCsv: boolean;
  exportCsvError: string | null;
  tabCounts: NegociacaoTabCounts;
  periodoPreview: { total: number | null; loading: boolean; requestId: string | null };
}

const initialState: NegociacoesState = {
  items: [],
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
  loading: false,
  error: null,
  isExportingCsv: false,
  exportCsvError: null,
  tabCounts: {
    all: 0,
    open: 0,
    concluded: 0,
    closed: 0,
    archived: 0,
    scheduled: 0,
    negotiationStarted: 0,
    active: 0,
    agreementClosed: 0,
    noAgreement: 0,
    paused: 0,
    failed: 0,
    withPendency: 0,
  },
  periodoPreview: { total: null, loading: false, requestId: null },
};

const negociacoesSlice = createSlice({
  name: 'negociacoes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearNegociacaoPeriodoPreview(state) {
      if (state.periodoPreview.total === null && !state.periodoPreview.loading) return;
      state.periodoPreview = { total: null, loading: false, requestId: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNegociacaoCasos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNegociacaoCasos.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.tabCounts = action.payload.tabCounts;
      })
      .addCase(fetchNegociacaoCasos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNegociacaoPeriodoPreview.pending, (state, action) => {
        state.periodoPreview = { total: null, loading: true, requestId: action.meta.requestId };
      })
      .addCase(fetchNegociacaoPeriodoPreview.fulfilled, (state, action) => {
        if (state.periodoPreview.requestId !== action.meta.requestId) return;
        state.periodoPreview = { total: action.payload, loading: false, requestId: null };
      })
      .addCase(fetchNegociacaoPeriodoPreview.rejected, (state, action) => {
        if (state.periodoPreview.requestId !== action.meta.requestId) return;
        state.periodoPreview = { total: null, loading: false, requestId: null };
      })
      .addCase(pauseNegociacaoCaso.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status;
        }
      })
      .addCase(resumeNegociacaoCaso.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status;
        }
      })
      .addCase(closeNegociacaoCaso.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status;
        }
      })
      .addCase(archiveNegociacaoCaso.fulfilled, (state, action) => {
        const index = state.items.findIndex((i) => i.id === action.payload.caseId);
        if (index < 0) {
          return;
        }

        state.items.splice(index, 1);
        state.total = Math.max(0, state.total - 1);
        state.totalPages = state.limit > 0 ? Math.ceil(state.total / state.limit) : 0;
      })
      .addCase(unarchiveNegociacaoCaso.fulfilled, (state, action) => {
        const index = state.items.findIndex((i) => i.id === action.payload.caseId);
        if (index < 0) {
          return;
        }

        state.items.splice(index, 1);
        state.total = Math.max(0, state.total - 1);
        state.totalPages = state.limit > 0 ? Math.ceil(state.total / state.limit) : 0;
      })
      .addCase(exportNegociacaoCasosCsv.pending, (state) => {
        state.isExportingCsv = true;
        state.exportCsvError = null;
      })
      .addCase(exportNegociacaoCasosCsv.fulfilled, (state) => {
        state.isExportingCsv = false;
      })
      .addCase(exportNegociacaoCasosCsv.rejected, (state, action) => {
        state.isExportingCsv = false;
        state.exportCsvError = action.payload as string;
      })
      .addCase(encerrarNegociacaoCaso.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status;
          item.tipoEncerramento = action.payload.tipoEncerramento;
          item.dataEncerramento = action.payload.dataEncerramento;
          item.permiteReabrir = action.payload.permiteReabrir;
        }
      })
      .addCase(reabrirNegociacaoCaso.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status;
          item.tipoEncerramento = null;
          item.dataEncerramento = null;
          item.permiteReabrir = true;
        }
      })
      .addCase(negociacaoCasoStatusUpdated, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload.caseId);
        if (item) {
          item.status = action.payload.status as NegociacaoCasoListItem['status'];
          if (action.payload.tipoEncerramento !== undefined) {
            item.tipoEncerramento = action.payload
              .tipoEncerramento as NegociacaoCasoListItem['tipoEncerramento'];
          }
        }
      });
  },
});

interface ListUiState {
  activeTab: NegociacoesTabValue;
  searchQuery: string;
  filters: NegociacoesFilters;
  sortConfig: NegociacoesSortConfig;
  page: number;
}

const listUiInitialState: ListUiState = {
  activeTab: 'ALL',
  searchQuery: '',
  filters: { status: [], temperature: [], pendency: [], situacao: [] },
  sortConfig: { key: 'createdAt', direction: 'desc' },
  page: 1,
};

const listUiSlice = createSlice({
  name: 'negociacoesListUi',
  initialState: listUiInitialState,
  reducers: {
    setListUiActiveTab(state, action: PayloadAction<NegociacoesTabValue>) {
      state.activeTab = action.payload;
      state.page = 1;
    },
    setListUiSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.page = 1;
    },
    setListUiFilters(state, action: PayloadAction<NegociacoesFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    setListUiSortConfig(state, action: PayloadAction<NegociacoesSortConfig>) {
      state.sortConfig = action.payload;
    },
    setListUiPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
  },
});

export const { clearNegociacaoPeriodoPreview } = negociacoesSlice.actions;

export const {
  setListUiActiveTab,
  setListUiSearchQuery,
  setListUiFilters,
  setListUiSortConfig,
  setListUiPage,
} = listUiSlice.actions;

interface SituacoesState {
  items: NegociacaoSituacaoOption[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

const situacoesInitialState: SituacoesState = {
  items: [],
  loading: false,
  error: null,
  loaded: false,
};

const situacoesSlice = createSlice({
  name: 'negociacoesSituacoes',
  initialState: situacoesInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNegociacaoSituacoes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNegociacaoSituacoes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.loaded = true;
      })
      .addCase(fetchNegociacaoSituacoes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro ao carregar situações';
      });
  },
});

const negociacoesReducer = combineReducers({
  list: negociacoesSlice.reducer,
  listUi: listUiSlice.reducer,
  situacoes: situacoesSlice.reducer,
  batch: batchNegociacaoReducer,
  lotes: lotesNegociacaoReducer,
  loteDetail: loteDetailReducer,
  ajustes: ajustesNegociacaoReducer,
});

export default negociacoesReducer;
